import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "sonner";
import PageLayout from "../../components/layouts/PageLayout";
import CustomNavbar from "../../components/common/CustomNavbar";
import { AppDispatch } from "../../store";
import {
    fetchBillingClients,
    fetchCreditLines,
    revokeCredit,
    shareCredit,
    shareCreditAll,
} from "../../features/whatsapp-onboarding/EmbeddedSignupServices";
import { BillingClient, CreditLine } from "../../types/EmbeddedSignupType";

type Tab = "all" | "on" | "attention";

const fmtDate = (d?: string) =>
    d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "";

export default function BillingAdmin() {
    const dispatch = useDispatch<AppDispatch>();
    const [clients, setClients] = useState<BillingClient[]>([]);
    const [lines, setLines] = useState<CreditLine[]>([]);
    const [tab, setTab] = useState<Tab>("all");
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState<number | "all" | null>(null);

    const load = useCallback(async () => {
        try {
            const [c, l] = await Promise.all([
                dispatch(fetchBillingClients({ page: 0, size: 20 })).unwrap(),
                dispatch(fetchCreditLines()).unwrap(),
            ]);
            setClients(c.results ?? []);
            setLines(l);
        } catch (e: any) {
            toast.error(e || "Could not load billing");
        } finally {
            setLoading(false);
        }
    }, [dispatch]);

    useEffect(() => {
        load();
    }, [load]);

    const act = async (id: number | "all", fn: () => Promise<unknown>, ok: string) => {
        setBusyId(id);
        try {
            await fn();
            toast.success(ok);
            await load();
        } catch (e: any) {
            toast.error(e || "Something went wrong");
        } finally {
            setBusyId(null);
        }
    };

    const attention = (c: BillingClient) => !c.onCreditLine && !!c.billingError;
    const counts = {
        all: clients.length,
        on: clients.filter((c) => c.onCreditLine).length,
        attention: clients.filter(attention).length,
    };
    const visible = useMemo(
        () =>
            clients.filter((c) => (tab === "on" ? c.onCreditLine : tab === "attention" ? attention(c) : true)),
        [clients, tab]
    );

    const tabs: [Tab, string][] = [
        ["all", `All ${counts.all}`],
        ["on", `On credit line ${counts.on}`],
        ["attention", `Needs attention ${counts.attention}`],
    ];

    return (
        <PageLayout
            customNavbar={
                <CustomNavbar>
                    <span className="text-lg font-bold uppercase">WhatsApp billing</span>
                </CustomNavbar>
            }
        >
            <div className="mx-auto max-w-5xl p-4 md:p-8">
                <div className="rounded-2xl bg-white p-6 shadow-lg md:p-10">
                    <header className="flex flex-wrap items-start justify-between gap-4">
                        <div>
                            <h2 className="text-3xl font-extrabold text-[#10231d] md:text-4xl">WhatsApp billing</h2>
                            <p className="mt-2 max-w-lg text-base text-gray-600">
                                Clients on Empedance's credit line are billed by Meta to Empedance and pay you from their wallet.
                            </p>
                        </div>
                        <button
                            className="rounded-lg bg-[#0f7b5a] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#0c6a4d] disabled:opacity-50"
                            disabled={busyId !== null}
                            onClick={() => act("all", () => dispatch(shareCreditAll()).unwrap(), "Credit shared with all clients")}
                        >
                            {busyId === "all" ? "Sharing…" : "Share with everyone missing it"}
                        </button>
                    </header>

                    <div className="mt-6 rounded-xl bg-[#f3f6f4] px-5 py-4">
                        <div className="text-lg font-semibold text-[#10231d]">Credit line at Meta</div>
                        {lines.length === 0 ? (
                            <p className="mt-1 text-sm text-gray-500">{loading ? "Loading…" : "No credit line found."}</p>
                        ) : (
                            lines.map((l) => (
                                <p key={l.id} className="mt-1 text-sm text-gray-700">
                                    <span className="font-semibold text-[#10231d]">{l.name}</span> id {l.id}
                                </p>
                            ))
                        )}
                    </div>

                    <div className="mt-6 inline-flex rounded-lg bg-[#f3f6f4] p-1">
                        {tabs.map(([k, label]) => (
                            <button
                                key={k}
                                onClick={() => setTab(k)}
                                className={`rounded-md px-4 py-1.5 text-sm font-medium transition ${
                                    tab === k ? "bg-white text-[#10231d] shadow-sm" : "text-gray-600"
                                }`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>

                    <div className="mt-4 overflow-x-auto">
                        <table className="w-full min-w-[640px] text-left text-sm">
                            <thead>
                                <tr className="border-b border-gray-200 text-gray-500">
                                    <th className="py-3 pr-4 font-medium">Business</th>
                                    <th className="py-3 pr-4 font-medium">Number</th>
                                    <th className="py-3 pr-4 font-medium">Billing</th>
                                    <th className="py-3 text-right font-medium">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visible.map((c) => {
                                    const warn = attention(c);
                                    return (
                                        <tr key={c.userId} className="border-b border-gray-200 align-top">
                                            <td className="py-4 pr-4">
                                                <div className="font-semibold text-[#10231d]">{c.businessName || "—"}</div>
                                                <div className="text-xs text-gray-500">
                                                    Client #{c.userId}, {c.connected ? "connected" : "disconnected"}
                                                </div>
                                            </td>
                                            <td className="py-4 pr-4">
                                                <div className="font-semibold text-[#10231d]">{c.phoneNumber || "—"}</div>
                                                {c.wabaId && <div className="text-xs text-gray-500">WABA {c.wabaId}</div>}
                                            </td>
                                            <td className="py-4 pr-4">
                                                <span
                                                    className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${
                                                        c.onCreditLine
                                                            ? "bg-emerald-100 text-emerald-800"
                                                            : warn
                                                            ? "bg-[#fdf1dc] text-amber-800"
                                                            : "bg-gray-100 text-gray-700"
                                                    }`}
                                                >
                                                    {c.onCreditLine ? "On credit line" : "Not on credit line"}
                                                </span>
                                                {c.onCreditLine && c.creditSharedAt && (
                                                    <div className="mt-1 text-xs text-gray-500">since {fmtDate(c.creditSharedAt)}</div>
                                                )}
                                                {warn && <div className="mt-1 max-w-[220px] text-xs text-red-700">{c.billingError}</div>}
                                            </td>
                                            <td className="py-4 text-right">
                                                {c.onCreditLine ? (
                                                    <button
                                                        className="rounded-lg border border-red-200 px-4 py-1.5 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
                                                        disabled={busyId !== null}
                                                        onClick={() =>
                                                            act(c.userId, () => dispatch(revokeCredit(c.userId)).unwrap(), "Credit revoked")
                                                        }
                                                    >
                                                        Revoke
                                                    </button>
                                                ) : c.connected ? (
                                                    <button
                                                        className="rounded-lg bg-[#0f7b5a] px-4 py-1.5 text-sm font-semibold text-white hover:bg-[#0c6a4d] disabled:opacity-50"
                                                        disabled={busyId !== null}
                                                        onClick={() =>
                                                            act(c.userId, () => dispatch(shareCredit(c.userId)).unwrap(), "Credit shared")
                                                        }
                                                    >
                                                        Share credit
                                                    </button>
                                                ) : null}
                                            </td>
                                        </tr>
                                    );
                                })}
                                {!loading && visible.length === 0 && (
                                    <tr>
                                        <td colSpan={4} className="py-10 text-center text-gray-500">
                                            No clients in this view.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </PageLayout>
    );
}